class BwsJudgeMeReviews extends HTMLElement {
  #controller;
  #sourceWriteReviewControl;
  #isOpeningReviewForm = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.querySelectorAll('[data-bws-write-review]').forEach((button) => {
      button.addEventListener('click', this.#openReviewForm, { signal: this.#controller.signal });
    });
    this.querySelector('[data-bws-review-form]')?.addEventListener('click', this.#handleReviewFormClick, {
      signal: this.#controller.signal,
    });
    this.#loadReviews();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  async #loadReviews() {
    const token = this.dataset.apiToken;
    const shopDomain = this.dataset.shopDomain;
    const externalId = this.dataset.productId;
    const productHandle = this.dataset.productHandle;
    const perPage = this.dataset.perPage || '10';

    if (!token || !shopDomain || (!productHandle && !externalId)) {
      this.#showError(this.dataset.missingConfigurationMessage);
      return;
    }

    const endpoint = new URL('https://api.judge.me/api/v1/widgets/product_review');
    endpoint.searchParams.set('shop_domain', shopDomain);
    if (productHandle) {
      endpoint.searchParams.set('handle', productHandle);
    } else {
      endpoint.searchParams.set('external_id', externalId);
    }
    endpoint.searchParams.set('page', '1');
    endpoint.searchParams.set('per_page', perPage);
    // Judge.me's public storefront token is intentionally browser-visible. Supplying it in the
    // query keeps this cross-origin request simple because the widget endpoint does not handle
    // the preflight generated by a custom authentication header.
    endpoint.searchParams.set('api_token', token);

    try {
      const response = await fetch(endpoint, {
        headers: {
          Accept: 'application/json, text/html',
        },
        signal: this.#controller.signal,
      });

      if (!response.ok) throw new Error(`Judge.me request failed with status ${response.status}`);

      const responseText = await response.text();
      const widgetHtml = this.#extractWidgetHtml(responseText);
      if (!widgetHtml) throw new Error('Judge.me returned an empty widget');

      this.#renderWidget(widgetHtml);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.warn('[BWS Judge.me reviews]', error);
      this.#showError(this.dataset.errorMessage);
    }
  }

  #extractWidgetHtml(responseText) {
    try {
      const payload = JSON.parse(responseText);
      return payload.widget || payload.html || payload.data?.widget || payload.data?.html || '';
    } catch {
      return responseText;
    }
  }

  #renderWidget(widgetHtml) {
    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(widgetHtml, 'text/html');
    const sourceWidget = documentFragment.querySelector('.jdgm-widget, .jdgm-rev-widg') || documentFragment.body;
    const sourceReviews = [...sourceWidget.querySelectorAll('.jdgm-rev')];
    const sourceContainer = this.querySelector('[data-bws-judge-me-source]');
    const reviewList = this.querySelector('[data-bws-review-list]');

    if (!sourceContainer || !reviewList) return;

    sourceContainer.replaceChildren(...[...documentFragment.body.childNodes].map((node) => node.cloneNode(true)));
    this.#sourceWriteReviewControl = [
      '.jdgm-write-rev-link',
      '[data-write-review]',
      'button[class*="write"]',
      'a[href*="review"]',
    ]
      .map((selector) => sourceContainer.querySelector(selector))
      .find(Boolean);
    window.jdgm?.initializeWidgets?.();

    reviewList.replaceChildren();
    sourceReviews.forEach((sourceReview) => reviewList.append(this.#buildReview(sourceReview)));

    const reviewCount = this.#getReviewCount(sourceWidget, sourceReviews.length);
    this.#updateCount(reviewCount);
    this.querySelector('[data-bws-review-loading]')?.setAttribute('hidden', '');
    this.querySelector('[data-bws-review-error]')?.setAttribute('hidden', '');

    if (sourceReviews.length === 0) {
      this.querySelector('[data-bws-review-empty]')?.removeAttribute('hidden');
      reviewList.setAttribute('hidden', '');
    } else {
      this.querySelector('[data-bws-review-empty]')?.setAttribute('hidden', '');
      reviewList.removeAttribute('hidden');
    }

    this.dataset.loaded = 'true';
  }

  #buildReview(sourceReview) {
    const article = document.createElement('article');
    article.className = 'bws-product-details-reviews__review';

    const meta = document.createElement('div');
    meta.className = 'bws-product-details-reviews__review-meta';

    const ratingSource = sourceReview.querySelector('.jdgm-rev__rating, [data-score]');
    const rating = this.#getRating(sourceReview, ratingSource);
    const stars = document.createElement('span');
    stars.className = 'bws-product-details-reviews__stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', this.dataset.ratingLabel.replace('__RATING__', String(rating)));
    stars.textContent = `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}`;
    meta.append(stars);

    const dateSource = sourceReview.querySelector('.jdgm-rev__timestamp, time');
    if (dateSource) {
      const time = document.createElement('time');
      const machineDate = dateSource.getAttribute('datetime') || dateSource.dataset.content;
      if (machineDate) time.setAttribute('datetime', machineDate);
      time.textContent = dateSource.textContent.trim();
      meta.append(time);
    }
    article.append(meta);

    const titleSource = sourceReview.querySelector('.jdgm-rev__title');
    if (titleSource?.textContent.trim()) {
      const title = document.createElement('h4');
      title.textContent = titleSource.textContent.trim();
      article.append(title);
    }

    const bodySource = sourceReview.querySelector('.jdgm-rev__body');
    if (bodySource) {
      const body = document.createElement('div');
      body.className = 'bws-product-details-reviews__review-body rte';
      body.innerHTML = bodySource.innerHTML;
      article.append(body);
    }

    const mediaSource = sourceReview.querySelector('.jdgm-rev__pics, .jdgm-rev__pic-link');
    if (mediaSource) {
      const media = mediaSource.cloneNode(true);
      media.classList.add('bws-product-details-reviews__review-media');
      article.append(media);
    }

    const replySource = sourceReview.querySelector('.jdgm-rev__reply');
    if (replySource) {
      const reply = document.createElement('div');
      reply.className = 'bws-product-details-reviews__review-reply';
      reply.innerHTML = replySource.innerHTML;
      article.append(reply);
    }

    const authorSource = sourceReview.querySelector('.jdgm-rev__author');
    const verifiedSource = sourceReview.querySelector('.jdgm-rev__buyer-badge');
    if (authorSource || verifiedSource) {
      const author = document.createElement('p');
      author.className = 'bws-product-details-reviews__review-author';
      if (authorSource) author.append(document.createTextNode(authorSource.textContent.trim()));
      if (verifiedSource?.textContent.trim()) {
        const verified = document.createElement('span');
        verified.className = 'bws-product-details-reviews__verified';
        verified.textContent = verifiedSource.textContent.trim();
        author.append(verified);
      }
      article.append(author);
    }

    return article;
  }

  #getRating(sourceReview, ratingSource) {
    const candidates = [
      ratingSource?.dataset.score,
      sourceReview.dataset.score,
      ratingSource?.getAttribute('aria-label'),
      ratingSource?.className,
    ];
    const value = candidates.map((candidate) => Number.parseFloat(String(candidate || '').match(/[1-5](?:\.\d+)?/)?.[0])).find(Number.isFinite);
    return Math.max(1, Math.min(5, Math.round(value || 5)));
  }

  #getReviewCount(sourceWidget, fallbackCount) {
    const countElement = sourceWidget.querySelector('[data-number-of-reviews]');
    const countText =
      countElement?.dataset.numberOfReviews ||
      sourceWidget.getAttribute('data-number-of-reviews') ||
      sourceWidget.querySelector('.jdgm-rev-widg__summary-text')?.textContent ||
      '';
    const countMatches = String(countText).match(/\d[\d,]*/g);
    const count = Number.parseInt(countMatches?.at(-1)?.replaceAll(',', '') || '', 10);
    return Number.isFinite(count) ? count : fallbackCount;
  }

  #updateCount(reviewCount) {
    const count = this.querySelector('[data-bws-review-count]');
    if (!count) return;
    const label = reviewCount === 1 ? this.dataset.reviewSingular : this.dataset.reviewPlural;
    count.textContent = `${reviewCount} ${label}`;
    count.removeAttribute('hidden');
  }

  #showError(message) {
    this.querySelector('[data-bws-review-loading]')?.setAttribute('hidden', '');
    const error = this.querySelector('[data-bws-review-error]');
    if (error) {
      error.textContent = message;
      error.removeAttribute('hidden');
    }
    this.#updateCount(0);
  }

  #openReviewForm = async () => {
    const formHost = this.querySelector('[data-bws-review-form]');
    const hostedForm = formHost?.querySelector('.jdgm-form-wrapper');
    if (formHost && hostedForm) {
      formHost.removeAttribute('hidden');
      hostedForm.style.display = 'block';
      formHost.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    if (this.#isOpeningReviewForm) return;
    this.#isOpeningReviewForm = true;
    this.#setReviewFormLoading(true);
    this.querySelector('[data-bws-review-form-error]')?.setAttribute('hidden', '');

    const nativeWidget = this.querySelector('#judgeme_product_reviews');
    const nativeWriteReviewControl = nativeWidget?.querySelector(
      '.jdgm-write-rev-link, [data-write-review], [class*="write-review"]'
    );

    if (nativeWidget && nativeWriteReviewControl instanceof HTMLElement) {
      await this.#waitForJudgeMeSetup();
      const formWasRendered = Boolean(nativeWidget.querySelector('.jdgm-form-wrapper'));
      nativeWriteReviewControl.click();

      const nativeForm = formWasRendered
        ? nativeWidget.querySelector('.jdgm-form-wrapper')
        : await this.#waitForElement(nativeWidget, '.jdgm-form-wrapper');

      if (nativeForm) {
        if (!formWasRendered) {
          nativeWidget.querySelector('.jdgm-write-rev-link')?.click();
        }
        formHost?.replaceChildren(nativeWidget);
        formHost?.removeAttribute('hidden');
        nativeForm.style.display = 'block';
        formHost?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        this.#isOpeningReviewForm = false;
        this.#setReviewFormLoading(false);
        return;
      }
    }

    if (this.#sourceWriteReviewControl instanceof HTMLAnchorElement) {
      const href = this.#sourceWriteReviewControl.getAttribute('href');
      if (href && href !== '#') {
        window.location.assign(this.#sourceWriteReviewControl.href);
        this.#isOpeningReviewForm = false;
        this.#setReviewFormLoading(false);
        return;
      }
    }

    const formError = this.querySelector('[data-bws-review-form-error]');
    if (formError) {
      formError.textContent = this.dataset.reviewFormErrorMessage;
      formError.removeAttribute('hidden');
    }
    this.#isOpeningReviewForm = false;
    this.#setReviewFormLoading(false);
  };

  #setReviewFormLoading(isLoading) {
    this.toggleAttribute('data-review-form-loading', isLoading);
    this.querySelectorAll('[data-bws-write-review]').forEach((button) => {
      button.toggleAttribute('disabled', isLoading);
      button.setAttribute('aria-busy', String(isLoading));
    });
  }

  #waitForJudgeMeSetup(timeout = 5000) {
    if (window.jdgm?._doneSetup) return Promise.resolve();

    return new Promise((resolve) => {
      const handleSetup = () => {
        window.clearTimeout(timeoutId);
        resolve();
      };
      const timeoutId = window.setTimeout(() => {
        document.removeEventListener('jdgm.doneSetup', handleSetup);
        resolve();
      }, timeout);
      document.addEventListener('jdgm.doneSetup', handleSetup, { once: true });
    });
  }

  #waitForElement(container, selector, timeout = 5000) {
    const existingElement = container.querySelector(selector);
    if (existingElement) return Promise.resolve(existingElement);

    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        const element = container.querySelector(selector);
        if (!element) return;
        window.clearTimeout(timeoutId);
        observer.disconnect();
        resolve(element);
      });
      const timeoutId = window.setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, timeout);
      observer.observe(container, { childList: true, subtree: true });
    });
  }

  #handleReviewFormClick = (event) => {
    if (!event.target.closest('.jdgm-cancel-rev')) return;
    const formHost = this.querySelector('[data-bws-review-form]');
    formHost?.setAttribute('hidden', '');
  };
}

if (!customElements.get('bws-judge-me-reviews')) {
  customElements.define('bws-judge-me-reviews', BwsJudgeMeReviews);
}
